import type { GetProps } from "tamagui";
import { webPressScale } from "../interactive/styles";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { Card, type CardProps } from "./Card";
type StyleOverrides = Pick<GetProps<typeof Card>, "borderColor" | "backgroundColor" | "hoverStyle" | "pressStyle">;
export type SelectionCardProps = CardProps & {
  selected?: boolean;
};

// Static portion — applies whenever `selected`, even when non-interactive.
const selectedStaticStyles: StyleOverrides = {
  borderColor: "$borderIntense"
};

// Interactive portion — gated on `isInteractive` to avoid hover bleed on static cards.
const selectedInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$surface2Hover",
    borderColor: "$borderIntenseHover"
  },
  pressStyle: {
    backgroundColor: "$surface2Hover",
    borderColor: "$borderIntenseHover",
    ...webPressScale
  }
};
const selectedDisabledStaticStyles: StyleOverrides = {
  borderColor: "$borderStrong"
};
const selectedDisabledInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong"
  },
  pressStyle: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong",
    scale: 1
  }
};

/**
 * Card with selection-state visual variants. Composes `Card` rather than
 * extending via `styled(Card)` — variant matching needs a styled config on
 * the base, and `Card` is a plain function component.
 */
export const SelectionCard = ({
  selected = false,
  ...rest
}: SelectionCardProps) => {
  const {
    isInteractive
  } = useInteractiveElement(rest);
  const {
    pressStyle: consumerPressStyle,
    hoverStyle: consumerHoverStyle,
    borderColor: consumerBorderColor,
    backgroundColor: consumerBackgroundColor,
    ...passthrough
  } = rest;

  // Variants merge on top of consumer values per sub-key. Conditional spread
  // on the JSX side: `prop={undefined}` overrides Tamagui's styled defaults
  // rather than falling through.
  const selectedActive = selected;
  const disabledSelectedActive = selected && rest.disabled === true;
  const variantPressStyle = selectedActive && isInteractive ? disabledSelectedActive ? selectedDisabledInteractiveStyles.pressStyle : selectedInteractiveStyles.pressStyle : undefined;
  const variantHoverStyle = selectedActive && isInteractive ? disabledSelectedActive ? selectedDisabledInteractiveStyles.hoverStyle : selectedInteractiveStyles.hoverStyle : undefined;
  const pressStyle = variantPressStyle != null || consumerPressStyle != null ? {
    ...consumerPressStyle,
    ...variantPressStyle
  } : undefined;
  const hoverStyle = variantHoverStyle != null || consumerHoverStyle != null ? {
    ...consumerHoverStyle,
    ...variantHoverStyle
  } : undefined;
  const borderColor = disabledSelectedActive ? selectedDisabledStaticStyles.borderColor : selectedActive ? selectedStaticStyles.borderColor : consumerBorderColor;
  return <Card {...passthrough} {...pressStyle !== undefined ? {
    pressStyle
  } : {}} {...hoverStyle !== undefined ? {
    hoverStyle
  } : {}} {...borderColor !== undefined ? {
    borderColor
  } : {}} {...consumerBackgroundColor !== undefined ? {
    backgroundColor: consumerBackgroundColor
  } : {}} data-sentry-element="Card" data-sentry-component="SelectionCard" data-sentry-source-file="SelectionCard.tsx" />;
};